import { createSlice } from '@reduxjs/toolkit';
import {
  subscribeAndAddPaymentMethodBuilder,
  changeClientStatusBuilder,
  createClientByVendorBuilder,
  createClientHashtagsBuilder,
  editVendorInfoBuilder,
  fetchAllVendorCategoriesBuilder,
  fetchVendorInfoBuilder,
  getAllClientsBuilder,
  getClientByIdBuilder,
  getClientsForVendorBuilder,
  getPaymentsIntentsForVendorBuilder,
  getVendorWorkingHoursBuilder,
  postVendorInfoBuilder,
  setClientNoteBuilder,
  setVendorWorkingHoursBuilder,
  addPaymentMethodBuilder,
  cancelSubscriptionBuilder,
  loginVendorBuilder,
  setClientWeddingDateBuilder,
  subscribeWithCouponBuilder,
  deleteLeadClientBuilder,
} from './builder';
import { LOGOUT, SUBSCRIPTION } from '../../constants';

const initialState = {
  userInfo: {
    id: '',
    name: '',
    email: '',
    address: '',
    showAddress: '',
    phoneNumber: '',
    instagram: '',
    facebook: '',
    tiktok: '',
    twitter: '',
    bannerPhoto: '',
    photos: [],
    links: [],
    workingHours: {},
    rating: null,
    reviews: [],
    googlePlaceLat: null,
    googlePlaceLong: null,
    googlePlaceId: null,
    hasSubscriptionTo: '',
    paymentMethod: {},
    subscription: {},
    websiteLink: '',
    onboardingFinishedAt: null,
    vendorCategory: null,
    hasCoupon: false,
    emailToShow: '',
  },
  allVendorCategories: {
    data: [],
  },
  allVendorClients: {
    data: [],
    maxReached: false,
    page: 1,
    total: 0,
    perPage: 0,
  },
  selectedClient: {
    data: [],
    getClientByIdPending: false,
  },

  allClients: {
    data: [],
  },
  paymentMethods: [],

  openSubscriptionModal: false,
  closeSubscriptionModalDisabled: false,

  paymentIntents: {
    data: [],
    maxReached: false,
    page: 1,
    total: 0,
    perPage: 0,
  },

  fetchAllVendorCategoryPending: false,
  createVendorPending: false,
  gettingVendorInfo: false,
  getClientsForVendorPending: false,
  setClientNotePending: false,
  changeClientStatusPending: false,
  getWorkingHoursPending: false,
  getAllClientsPending: false,
  editVendorPending: false,
  createClientByVendorPending: false,
  createClientHashtagsPending: false,
  addPaymentMethodPending: false,
  getPaymentIntentsForVendorPending: false,
  subscribeAndAddPaymentMethodPending: false,
  cancelSubscriptionPending: false,
  subscribeWithCouponPending: false,
  deleteLeadClientPending: false,
};

export const vendorSlice = createSlice({
  name: 'vendorsInfo',
  initialState,
  reducers: {
    setNameInput: (state, { payload }) => {
      state.name = payload;
    },
    setOpenSubscriptionModal: (state, { payload }) => {
      state.openSubscriptionModal = payload;
    },
  },
  extraReducers: builder => {
    fetchAllVendorCategoriesBuilder(builder);
    fetchVendorInfoBuilder(builder);
    loginVendorBuilder(builder);
    getClientsForVendorBuilder(builder);
    postVendorInfoBuilder(builder);
    getClientByIdBuilder(builder);
    setClientNoteBuilder(builder);
    changeClientStatusBuilder(builder);
    getVendorWorkingHoursBuilder(builder);
    setVendorWorkingHoursBuilder(builder);
    getAllClientsBuilder(builder);
    editVendorInfoBuilder(builder);
    createClientByVendorBuilder(builder);
    createClientHashtagsBuilder(builder);
    subscribeAndAddPaymentMethodBuilder(builder);
    getPaymentsIntentsForVendorBuilder(builder);
    addPaymentMethodBuilder(builder);
    cancelSubscriptionBuilder(builder);
    setClientWeddingDateBuilder(builder);
    subscribeWithCouponBuilder(builder);
    deleteLeadClientBuilder(builder);

    builder.addCase(LOGOUT, () => initialState);

    builder.addCase(SUBSCRIPTION, (state, action) => {
      state.openSubscriptionModal = true;
    });
  },
});

export const selectAllVendorCategories = state => state.vendorsInfo.allVendorCategories;
export const selectAllVendorCategoriesData = state => state.vendorsInfo.allVendorCategories.data;
export const selectFetchAllVendorCategoryPending = state => state.vendorsInfo.fetchAllVendorCategoryPending;
export const selectVendorsInfo = state => state.vendorsInfo;
export const createVendorPending = state => state.vendorsInfo.createVendorPending;
export const selectUserInfo = state => state.vendorsInfo.userInfo;
export const selectClient = state => state.vendorsInfo.selectedClient;
export const selectClientData = state => state.vendorsInfo.selectedClient.data;
export const selectAllVendorClientsData = state => state.vendorsInfo.allVendorClients.data;
export const selectAllVendorClients = state => state.vendorsInfo.allVendorClients;
export const selectSetClientNotePending = state => state.vendorsInfo.setClientNotePending;
export const selectChangeClientStatusPending = state => state.vendorsInfo.changeClientStatusPending;
export const selectGetWorkingHoursPending = state => state.vendorsInfo.getWorkingHoursPending;
export const selectAllClients = state => state.vendorsInfo.allClients;
export const selectGetAllClientsPending = state => state.vendorsInfo.getAllClientsPending;
export const selectEditVendorPending = state => state.vendorsInfo.editVendorPending;
export const selectCreateClientByVendorPending = state => state.vendorsInfo.createClientByVendorPending;
export const selectCreateClientHashtagsPending = state => state.vendorsInfo.createClientHashtagsPending;

export const selectAddPaymentMethodPending = state => state.vendorsInfo.addPaymentMethodPending;
export const selectSubscribeAndAddPaymentMethodPending = state => state.vendorsInfo.subscribeAndAddPaymentMethodPending;

export const selectOpenSubscriptionModal = state => state.vendorsInfo.openSubscriptionModal;
export const selectPaymentIntents = state => state.vendorsInfo.paymentIntents;
export const selectGetPaymentIntentsForVendorPending = state => state.vendorsInfo.getPaymentIntentsForVendorPending;
export const selectCancelSubscriptionPending = state => state.vendorsInfo.cancelSubscriptionPending;

export const selectCloseSubscriptionModalDisabled = state => state.vendorsInfo.closeSubscriptionModalDisabled;

export const selectSubscribeWithCouponPending = state => state.vendorsInfo.subscribeWithCouponPending;

export const selectDeleteLeadClientPending = state => state.vendorsInfo.deleteLeadClientPending;

export const { setNameInput, setOpenSubscriptionModal } = vendorSlice.actions;

export default vendorSlice.reducer;
